// Mirrors crm.members / crm.point_transactions / business_loyalty_settings /
// member_store_points / loyalty_configs / rewards / vouchers / promos in
// CliqSuite_DB_Architecture.dc.html.
import { z } from "zod";

export const memberSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  home_store_id: z.string().uuid(),
  member_number: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  telegram_handle: z.string().nullable(),
  telegram_id: z.number().nullable(),
  email: z.string().nullable(),
  avatar_color: z.string().nullable(),
  is_active: z.boolean().default(true),
  joined_at: z.string(),
  last_visit_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Member = z.infer<typeof memberSchema>;

export const pointTransactionSchema = z.object({
  id: z.string().uuid(),
  member_id: z.string().uuid(),
  store_id: z.string().uuid(),
  order_id: z.string().uuid().nullable(),
  transaction_type: z.enum(["earn", "redeem", "expire", "manual_add", "manual_deduct"]),
  points_delta: z.number().int(),
  points_after: z.number().int(),
  description: z.string().nullable(),
  performed_by_staff_id: z.string().uuid().nullable(),
  created_at: z.string(),
});
export type PointTransaction = z.infer<typeof pointTransactionSchema>;

export const businessLoyaltySettingSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  share_customers: z.boolean().default(false),
  share_points: z.boolean().default(false),
  created_at: z.string(),
  updated_at: z.string(),
});
export type BusinessLoyaltySetting = z.infer<typeof businessLoyaltySettingSchema>;

// Current point balance per member per store — the real source of truth
// for "how many points does this member have here", instead of deriving
// it by replaying crm.point_transactions history client-side.
export const memberStorePointSchema = z.object({
  id: z.string().uuid(),
  member_id: z.string().uuid(),
  store_id: z.string().uuid(),
  points: z.number().int().default(0),
  lifetime_points: z.number().int().default(0),
  tier: z.string().default("Bronze"),
  updated_at: z.string(),
});
export type MemberStorePoint = z.infer<typeof memberStorePointSchema>;

export const loyaltyConfigSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  is_active: z.boolean().default(true),
  earn_rate: z.string().default("1.0"),
  // Points a new member starts with when they join at this store.
  welcome_points: z.number().int().min(0).default(0),
  // Off: a sale with any discount earns no points.
  earn_on_discounted: z.boolean().default(true),
  // Points that take 1,000 off a bill at the register; 0 = points can't be spent as money.
  redeem_rate: z.number().int().min(0).default(0),
  // A balance with no earn or redeem for this many months is cleared; null = never.
  expiry_months: z.number().int().positive().nullable().default(null),
  tiers: z.array(z.record(z.string(), z.unknown())).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});
export type LoyaltyConfig = z.infer<typeof loyaltyConfigSchema>;

export const rewardSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  points_cost: z.number().int().positive(),
  reward_type: z.enum(["voucher_flat", "voucher_pct", "freebie"]),
  discount_value: z.number().int().nullable(),
  max_redemptions: z.number().int().nullable(),
  redemption_count: z.number().int().default(0),
  is_active: z.boolean().default(true),
  valid_from: z.string().nullable(),
  valid_until: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Reward = z.infer<typeof rewardSchema>;

export const voucherSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  member_id: z.string().uuid().nullable(),
  reward_id: z.string().uuid().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  discount_type: z.enum(["flat", "percentage"]),
  discount_value: z.number().int(),
  min_order: z.number().int().default(0),
  status: z.enum(["active", "used", "expired"]).default("active"),
  used_in_order_id: z.string().uuid().nullable(),
  used_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Voucher = z.infer<typeof voucherSchema>;

export const promoSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  headline: z.string().nullable(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  promo_type: z.enum(["percentage", "flat", "bogo"]),
  discount_value: z.number().int(),
  applies_to: z.enum(["all", "category", "product"]).default("all"),
  target_category_id: z.string().uuid().nullable(),
  target_product_id: z.string().uuid().nullable(),
  min_order: z.number().int().default(0),
  code: z.string().nullable(),
  schedule_type: z.enum(["always", "time_window", "days"]).default("always"),
  schedule_start: z.string().nullable(),
  schedule_end: z.string().nullable(),
  schedule_days: z.array(z.number().int()).nullable(),
  is_active: z.boolean().default(true),
  valid_from: z.string().nullable(),
  valid_until: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Promo = z.infer<typeof promoSchema>;

/** What a voucher takes off `base` (the bill, after any offer). Register, Guest Portal and sync-api all use this. */
export function voucherDiscount(v: Pick<Voucher, "discount_type" | "discount_value">, base: number): number {
  return v.discount_type === "percentage" ? Math.round((base * v.discount_value) / 100) : Math.min(v.discount_value, base);
}
/** A voucher that can go on a bill of this size right now: still active, not expired, and the bill reaches its minimum. */
export function voucherUsable(v: Pick<Voucher, "status" | "min_order" | "expires_at">, subtotal: number, now = Date.now()): boolean {
  return v.status === "active" && subtotal >= v.min_order && !(v.expires_at && now > new Date(v.expires_at).getTime());
}

/** What an offer takes off a bill, before capping it at the bill. Register, Guest Portal and sync-api all use this. */
export function promoDiscount(p: Pick<Promo, "promo_type" | "discount_value">, subtotal: number, lines: Array<{ unitPrice: number; qty: number }>): number {
  if (p.promo_type === "percentage") return Math.round((subtotal * p.discount_value) / 100);
  if (p.promo_type === "flat") return Math.min(p.discount_value, subtotal);
  // Buy one, get one: the cheapest unit is free, once there are two.
  const units = lines.flatMap((l) => Array.from({ length: l.qty }, () => l.unitPrice));
  return units.length < 2 ? 0 : Math.min(...units);
}

/** Whether an offer is on for this bill right now, by this device's own clock (dates, weekdays and the daily time window). */
export function promoEligible(p: Promo, subtotal: number, now = new Date()): boolean {
  if (!p.is_active || subtotal < p.min_order) return false;
  const iso = now.toISOString();
  if (p.valid_from && iso < p.valid_from) return false;
  if (p.valid_until && iso > p.valid_until) return false;
  if (p.schedule_type === "days" && p.schedule_days?.length && !p.schedule_days.includes(now.getDay())) return false;
  if (p.schedule_type === "time_window" && p.schedule_start && p.schedule_end) {
    const clock = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    if (clock < p.schedule_start.slice(0, 5) || clock >= p.schedule_end.slice(0, 5)) return false;
  }
  return true;
}

// A member's tier is never stored: it is the highest tier of the store's loyalty program whose
// threshold their lifetime points at that store reach. member_store_points.tier is a leftover
// that nothing reads. Owner, Guest and sync-api all go through these two functions.
// ponytail: per store only. "Share points across stores" is not honoured anywhere yet; sum the
// member's lifetime points across stores here when it is.
export type LoyaltyTier = { name: string; min: number; color: string };

const TIER_COLOR_BY_NAME: Record<string, string> = { bronze: "#C2773F", silver: "#8A8F9B", gold: "#E0A93C", platinum: "#7C6FD0" };
const DEFAULT_LOYALTY_TIERS: LoyaltyTier[] = [
  { name: "Bronze", min: 0, color: "#C2773F" },
  { name: "Silver", min: 500, color: "#8A8F9B" },
  { name: "Gold", min: 2000, color: "#E0A93C" },
];

/** Reads both stored shapes, {name, min, color} (Owner) and the database default {key, min_pts, icon}; lowest tier first. */
export function loyaltyTiers(raw: unknown): LoyaltyTier[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_LOYALTY_TIERS;
  return raw
    .map((t) => {
      const r = t as Record<string, unknown>;
      const name = String(r.name ?? r.key ?? "Tier");
      return { name, min: Number(r.min ?? r.min_pts ?? 0) || 0, color: String(r.color ?? TIER_COLOR_BY_NAME[name.toLowerCase()] ?? "#9AA3AF") };
    })
    .sort((a, b) => a.min - b.min);
}

export function tierFor(tiers: LoyaltyTier[], lifetimePoints: number): LoyaltyTier {
  return tiers.filter((t) => lifetimePoints >= t.min).pop() ?? tiers[0]!;
}
